import React, { lazy, useState } from "react";
import FAQ from "../../pages/Faq";
import { useLocation, useNavigate } from "react-router-dom";

import { Helmet } from "react-helmet-async";
import {
  FaUserTie,
  FaBriefcase,
  FaRegFileLines,
  FaUserCheck,
  FaCoins,
  FaArrowsRotate,
  FaFileSignature,
  FaPenNib,
  FaEnvelopeOpenText,
  FaEnvelope,
  FaCaretRight,
} from "react-icons/fa6";

import {
  FiClock,
  FiFileText,
  FiAlertTriangle,
  FiMessageCircle,
  FiUsers,
  FiBriefcase,
} from "react-icons/fi";
import { Check } from "lucide-react";
import OfferLetterGeneratorForm from "./OfferLetterGeneratorForm";

const steps = [
  {
    number: 1,
    title: "Enter Company Details",
    description:
      "Add the company name, address and authorised signatory information",
  },
  {
    number: 2,
    title: "Add Candidate Information",
    description: "Enter the selected candidate’s full name and contact details",
  },
  {
    number: 3,
    title: "Provide Job Details",
    description:
      "Add the job title, department, employment type, location and joining date",
  },
  {
    number: 4,
    title: "Enter Salary Information",
    description:
      "Provide the annual CTC, monthly salary, bonuses and applicable benefits",
  },
  {
    number: 5,
    title: "Add Employment Terms",
    description:
      "Enter the probation period, notice period, working schedule and offer validity date",
  },
  {
    number: 6,
    title: "Generate the Letter",
    description:
      "The system will insert the information into the standard offer letter format",
  },
  {
    number: 7,
    title: "Review and Issue",
    description:
      "Check all names, dates, salary details and conditions before sharing the letter with the candidate",
  },
];
const features = [
  <>
    <strong>Startups:</strong> Create professional offer letters for new hires
    without needing a dedicated HR team or complicated documentation process
  </>,
  <>
    <strong>HR Teams:</strong> Generate consistent offer letters for different
    roles and employees while keeping important hiring details organised and
    easy to manage
  </>,
  <>
    <strong>Small and Medium-Sized Businesses:</strong> Prepare offer letters
    quickly for new employees without spending time creating and formatting each
    document manually
  </>,
  <>
    <strong>Business Owners:</strong> Create clear and professional job offers
    for selected candidates, even when hiring and HR responsibilities are
    managed internally
  </>,
  <>
    <strong>Consultants and CA Firms:</strong> Prepare structured offer letters
    and support clients with their day-to-day employee hiring and documentation
    requirements
  </>,
  <>
    <strong>Recruitment Agencies:</strong> Generate offer letters for selected
    candidates quickly and provide clients with a consistent format for
    completing the hiring process
  </>,
];
const Faqs1 = [
  {
    title: "Can I Create an Offer Letter Online for Free?",
    content:
      "Yes. You can create a professional offer letter online by entering the required company and candidate details. The generator provides a structured format, so you can prepare an offer letter without creating the document manually.",
  },
  {
    title: "Can I Create an Offer Letter Without Login?",
    content:
      "Yes. You can use the Waggex Offer Letter Generator without creating an account or logging in. Enter the required details, generate your offer letter, and use the completed document for your hiring process.",
  },
  {
    title: "Can I Add Company and Candidate Details to the Offer Letter?",
    content:
      "Yes. You can add important information such as the company name, candidate name, job title, salary, joining date, and other relevant employment details to personalise the offer letter.",
  },
  {
    title: "Can I Download the Generated Offer Letter?",
    content:
      "Yes. Once you complete the required details, the generator creates your offer letter in a structured format that you can download and use for sharing with the selected candidate.",
  },
  {
    title: "Can I Use the Offer Letter for Different Job Roles?",
    content:
      "Yes. The offer letter can be used for different positions and hiring requirements. Add the relevant candidate, job, salary, joining, and employment details based on the role you are hiring for.",
  },
  {
    title: "Can I Share the Offer Letter With the Selected Candidate?",
    content:
      "Yes. After generating the offer letter, you can share the completed document with the selected candidate. This makes it easier to communicate the key terms of the job offer before the candidate joins.",
  },
];
const schema = [
  // =========================================================
  // SCHEMA 1: WebApplication
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Waggex Free Offer Letter Generator",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Free online offer letter generator. Enter company and candidate details, job role, salary, and employment terms to create and download a professional offer letter PDF instantly.",
    url: "https://www.waggex.com/hr-documents/offer-letter-generator",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    featureList: [
      "Free offer letter generator India",
      "Online offer letter format",
      "Offer letter PDF download India",
      "Job offer letter template India",
      "Professional offer letter maker India",
    ],
  },

  // =========================================================
  // SCHEMA 2: FAQPage
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is an offer letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "An Offer Letter is a formal document issued by an employer to a selected candidate. It outlines the proposed job, salary, joining date, and basic employment conditions before the candidate officially joins.",
        },
      },
      {
        "@type": "Question",
        name: "What details are included in a job offer letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A standard job offer letter includes candidate name, job title, department, joining date, employment type, salary and CTC, allowances, benefits, probation period, notice period, and pre-joining conditions.",
        },
      },
      {
        "@type": "Question",
        name: "Can salary and CTC be included?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. The generator includes dedicated salary fields for gross salary, CTC, salary components, and allowances. The offer letter presents these figures in a clearly formatted salary section.",
        },
      },
      {
        "@type": "Question",
        name: "Is an offer letter the same as a letter of employment?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Not exactly. An offer letter is issued before the candidate joins. A letter of employment or appointment letter is issued when the candidate officially joins and contains more detailed employment terms.",
        },
      },
      {
        "@type": "Question",
        name: "Can I use the same format for different employees?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. The generator allows offer letters for different candidates with different roles, salaries, and terms each time. The template format stays consistent while the content changes per candidate.",
        },
      },
    ],
  },

  // =========================================================
  // SCHEMA 3: Organization
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides free HR document generation tools and HRMS software for Indian businesses.",
    areaServed: "IN",
  },
];

const offerLetterContent = [
  {
    icon: FaUserTie,
    title: "Company and Candidate Details",
    description:
      "The company name, address, issue date and candidate information.",
  },
  {
    icon: FaBriefcase,
    title: "Employment Offer",
    description:
      "A formal statement offering the candidate a position within the organisation.",
  },
  {
    icon: FaRegFileLines,
    title: "Job Details",
    description:
      "The designation, department, employment type, reporting manager and work location.",
  },
  {
    icon: FaUserCheck,
    title: "Joining Information",
    description:
      "The expected date of joining and any onboarding instructions.",
  },
  {
    icon: FaCoins,
    title: "Salary and Benefits",
    description:
      "The annual CTC, monthly salary, bonus, incentives and employee benefits.",
  },
  {
    icon: FaArrowsRotate,
    title: "Probation and Notice Period",
    description:
      "The probation duration and applicable notice-period conditions.",
  },
  {
    icon: FaFileSignature,
    title: "Offer Acceptance",
    description:
      "It allowing the candidate to accept & acknowledge the proposed employment terms.",
  },
  {
    icon: FaPenNib,
    title: "Authorised Signature",
    description:
      "The name and designation of the employer’s authorised representative.",
  },
];
const offerLetterPoints = [
  "Job title and department",
  "Employment type",
  "Date of joining",
  "Salary and benefits",
  "Probation period",
  "Offer acceptance deadline",
];

const companystepsFeatures = [
  {
    id: 1,
    icon: <FiClock />,
    title: "Save HR Time",
    desc: "Create professional offer letters quickly without drafting each letter from scratch or repeatedly copying candidate details",
  },
  {
    id: 2,
    icon: <FiFileText />,
    title: "Maintain a Standard Format",
    desc: "Use a consistent offer letter format for different candidates while keeping important job and employment details clearly organised",
  },
  {
    id: 3,
    icon: <FiAlertTriangle />,
    title: "Reduce Manual Errors",
    desc: "Enter candidate and employment details once and place them in the relevant sections of the offer letter without repeated manual editing",
  },
  {
    id: 4,
    icon: <FiMessageCircle />,
    title: "Improve Candidate Communication",
    desc: "Clearly share important details such as the job role, salary, joining date, and other employment terms with selected candidates",
  },
  {
    id: 5,
    icon: <FiUsers />,
    title: "Support Faster Hiring",
    desc: "Generate and share offer letters soon after selecting a candidate, helping you complete the hiring process without unnecessary delays",
  },
  {
    id: 6,
    icon: <FiBriefcase />,
    title: "Suitable for Different Businesses",
    desc: "Create offer letters for your hiring needs, whether you’re a startup, small business, recruiter, or growing organisation",
  },
];

const offerPoints = [
  "Proposed designation",
  "Salary and benefits",
  "Work location",
  "Joining date",
  "Basic employment terms",
];

const appointmentPoints = [
  "Duties and responsibilities",
  "Leave entitlement",
  "Confidentiality",
  "Workplace policies",
  "Notice period",
  "Termination conditions",
];
export default function OfferLetterGenerator() {
  return (
    <>
      <Helmet>
        <title>Free Offer Letter Generator Online | Waggex</title>
        <meta
          name="description"
          content="Create and download free professional offer letter online in minutes. Add candidate, role, salary and CTC details, then download your PDF instantly"
        />
        <meta
          name="keywords"
          content="Offer letter generator online,offer letter format India,free offer letter maker online India,job offer letter template India,offer letter PDF generator India,professional offer letter format India,employee offer letter creator India,offer letter Word format India,online job offer letter India"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/hr-documents/offer-letter-generator"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <div className="px-2 py-3 sm:px-4">
        <section className="rounded-xl border border-white px-2 sm:px-5 py-7 shadow-sm backdrop-blur bg-[#ffffff40]">
          <div className="text-center  mb-10">
            <h1 className="text-[#10324F] text-lg sm:text-3xl font-roboto font-bold leading-snug mb-4">
              Generate Free Offer Letter Online
            </h1>

            <p className="mx-auto mt-8 sm:max-w-166 md:max-w-3xl text-sm sm:text-lg leading-6 text-[#10324F] min-h-18 sm:min-h-12">
              Create a professional offer letter online using a ready-to-use
              template. Simply Add candidate, job, salary, and joining details,
              then generate and download your offer letter as a PDF for free
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row mb-10">
              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
              >
                Start Free Trial
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
              >
                Book a Demo
              </button>
            </div>
          </div>
          <OfferLetterGeneratorForm />
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 ">
                {/* Left Content */}
                <div>
                  <div className="mt-4">
                    <div className="mb-6 flex items-center gap-3">
                      <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                      <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                        WHAT IS AN OFFER LETTER?
                      </span>
                    </div>

                    <h2 className="text-xl md:text-3xl font-semibold text-[#10324F] mt-3 mb-5">
                      Create a Professional Job Offer in Minutes
                    </h2>

                    <p className="text-[#10324F] mb-6 leading-relaxed max-w-2xl">
                      Create and share professional Offer Letters without
                      starting from scratch. Add the candidate’s role, salary,
                      joining date, company details, and other employment terms
                      in a structured format. A clear and well-organized offer
                      letter helps candidates understand the opportunity,
                      reduces manual paperwork, and makes the hiring process
                      faster and more professional for your HR team
                    </p>
                  </div>
                </div>

                {/* Right Content */}
                <div className="flex flex-col">
                  {offerLetterPoints.map((item, index) => (
                    <React.Fragment key={item}>
                      <div className="relative z-10 flex min-h-12.5 items-center justify-center rounded-lg border border-white  bg-linear-to-b from-white to-[#D1DFEA]/80  hover:bg-white/90 px-4 py-3 text-center text-sm font-medium text-[#10324F] shadow-sm backdrop-blur-sm transition sm:min-h-12.5 sm:text-base">
                        {item}
                      </div>

                      {index < offerLetterPoints.length - 1 && (
                        <div className="mx-auto h-6 border-l border-dashed border-[#10324F]/80" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  WHAT DOES THE OFFER LETTER TEMPLATE INCLUDE?
                </p>
                <h2 className="mt-4 text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
                  Create Professional Offer Letters With Your Details
                </h2>

                <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Use this online offer letter generator to create a
                  professionally structured offer letter with your candidate and
                  company details. Generate the letter quickly and share it
                  directly with the selected candidate
                </p>
              </div>

              {/* Cards */}
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {offerLetterContent.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="group flex min-h-25.5 items-start gap-4 rounded-xl border border-white/90 bg-linear-to-b from-white/60 to-[#D1DFEA]/70  hover:bg-white/90 px-5 py-5 transition-all duration-200 shadow-sm"
                    >
                      {/* Icon */}
                      <div className="flex shrink-0 items-center justify-center pt-1">
                        <Icon className="text-2xl text-[#10324F]" />
                      </div>

                      {/* Content */}
                      <div>
                        <h3 className="text-sm sm:text-lg font-semibold leading-5 text-[#10324F]">
                          {item.title}
                        </h3>

                        <p className="mt-2 text-xs sm:text-sm leading-5 text-[#10324F]">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  HOW TO CREATE AN OFFER LETTER ONLINE
                </p>

                <h2 className="mt-4 text-xl font-bold leading-tight text-[#10324F] sm:text-3xl">
                  Create Your Letter in Simple Steps
                </h2>
              </div>

              {/* Steps */}

              {/* ROW 1 */}
              <div className=" mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(0, 4).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE */}
                    <div className="absolute left-0 right-1/2 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#10324F] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#1324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ROW 2 */}
              <div className=" mt-4 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(4, 7).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE — IMPORTANT FOR STEP 5 */}
                    <div className="absolute left-0 right-1/2 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 2 && (
                      <div className="absolute left-1/2 right-0 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#163957] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#10324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-14">
                {/* LEFT CONTENT */}
                <div>
                  {/* Small heading */}
                  {/* <div className="mb-6 flex items-center gap-4">
                    <span className="h-px w-11 bg-[#10324F]" />

                    <span className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                      Understand the Difference
                    </span>
                  </div> */}
                  <div className="mb-6 flex items-center gap-3">
                    <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                      UNDERSTAND THE DIFFERENCE
                    </span>
                  </div>

                  {/* Main heading */}
                  <h2 className="text-xl font-semibold leading-tight text-[#10324F] sm:text-3xl">
                    Difference Between Offer Letter vs Appointment Letter
                  </h2>

                  {/* Intro */}
                  <p className="mt-7 max-w-2xl text-sm leading-8 text-[#10324F]">
                    An Offer Letter and Appointment Letter serve different
                    purposes in the hiring process. Understanding the difference
                    helps HR teams issue the right document at the right stage
                    and keep employee documentation clear and organised
                  </p>

                  {/* Explanation points */}
                  <div className="mt-9 space-y-7">
                    {/* Offer Letter */}
                    <div className="flex items-start gap-5">
                      <FaCaretRight className="mt-1.5 shrink-0 text-2xl text-[#10324F]" />

                      <div className="text-sm leading-8 text-[#10324F]">
                        <strong>Offer Letter</strong>

                        <ul className="mt-1 list-disc pl-5 space-y-1">
                          <li>
                            Usually shared after a candidate is selected and
                            before joining to communicate the job offer and
                            proposed employment terms
                          </li>
                          <li>
                            Helps the candidate understand key details such as
                            the role, salary, joining date, and other conditions
                            before accepting the offer
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Appointment Letter */}
                    <div className="flex items-start gap-5">
                      <FaCaretRight className="mt-1.5 shrink-0 text-2xl text-[#10324F]" />

                      <div className="text-sm leading-8 text-[#10324F]">
                        <strong>Appointment Letter</strong>

                        <ul className="mt-1 list-disc pl-5 space-y-1">
                          <li>
                            Generally issued when employment is formally
                            confirmed, usually around the time the employee
                            joins the organisation
                          </li>
                          <li>
                            Covers detailed employment terms, policies,
                            responsibilities, and other conditions that apply
                            during employment
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT COMPARISON */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* ================= OFFER LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-5 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelopeOpenText className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Offer Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b  from-white/60 to-[#D1DFEA]/30 px-3 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {offerPoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ================= APPOINTMENT LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-5 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelope className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Appointment Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-3 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {appointmentPoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHY USE FREE OFFER LETTER GENERATOR?
                </p>
                <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#10324F]  uppercase">
                  Create Professional Hiring Documents Faster
                </h2>
                {/* <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Send HR announcements, payroll updates, policy changes,
                  holiday notices, attendance reminders, event updates, and
                  important company communications from one platform
                </p> */}
              </div>
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4">
                {companystepsFeatures.map((item) => {
                  // const Icon = item.icon;

                  return (
                    <div
                      key={item.id}
                      className="rounded-xl border border-white/90  bg-linear-to-b from-white/60 to-[#D1DFEA]/60  hover:bg-white/30 p-4 duration-200 shadow-sm transition-all"
                    >
                      {/* Top Row */}
                      <div className="flex items-center justify-center mb-5">
                        {/* <div className="w-14 h-10 rounded-md bg-[#16324F] font-roboto text-white flex items-center justify-center text-lg font-bold shadow">
                          {item.id}
                        </div> */}
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10324F] text-xl text-white">
                          {item.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <h2 className="text-center text-sm sm:text-base font-semibold text-[#10324F] font-roboto">
                        {item.title}
                      </h2>

                      <p className="mt-2 text-center text-sm leading-6 text-[#10324F] font-roboto">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto mb-4">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHO CAN USE THIS OFFER LETTER FORMAT?
                </p>
                <h2 className="font-roboto text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide">
                  Create an Offer Letter - No Signup Required
                </h2>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 place-items-start">
                {features.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 text-[#10324F] px-2 py-3  rounded-2xl"
                  >
                    {/* Check Icon */}
                    <div className="mt-1 flex items-center justify-center text-[#10324F]">
                      <Check size={30} aria-hidden="true" />
                    </div>

                    {/* Text */}
                    <p className="font-roboto text-sm sm:text-lg font-medium leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <FAQ
            title="Frequently Asked Question"
            items={Faqs1}
            defaultOpenIndex={0}
          />
          <section className="w-full bg-[#F4F6F8]/60 px-6 py-10 rounded-xl border border-white mt-8">
            <div className="max-w-6xl mx-auto text-center mt-4 mb-4">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold uppercase">
                Create Your Offer Letter Online
              </h2>

              <p className="mt-4 text-[#10324F] text-sm sm:text-base">
                Enter the candidate, job and salary details to create a clear
                and professionally structured Employee Offer Letter
              </p>

              <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3
              rounded-xl
              bg-[#10324F]
              text-white text-xs sm:text-sm
              font-medium
              shadow-md
              transition-all duration-300
              hover:scale-105
            "
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                >
                  Generate Offer Letter
                </button>

                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3 text-xs sm:text-sm
              rounded-xl
              border border-[#10324F]
              text-[#10324F]
              font-medium
              transition-all duration-300
              hover:bg-white
              hover:text-[#16385C]
            "
                  onClick={() => (window.location.href = "/all-hr-documents")}
                >
                  Explore more
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>
    </>
  );
}
